import { useCallback } from 'react';
import { BulkNotificationHistoryActions, NotificationTray } from '@frontend/notifications';
import { PanelPortal, PanelContent } from '@frontend/panel-engine';
import { useIsCompactMode } from '@frontend/responsiveness';
import { useScopedAppFlagStore } from '@frontend/scope';
import { SurfaceHeader } from '@frontend/surface-header';
import { useTopBarShallowStore } from '../../store/use-top-bar-store';
import { labelMap } from '../../utils/label-map';

interface NotificationsPanelProps {
  onClose: () => void;
  onLocationTrayOpen: (e: React.MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => void;
  showNotificationFilterAsChips?: boolean;
  showForYouNotificationsTab?: boolean;
}

export const NotificationsPanel = ({
  onClose,
  onLocationTrayOpen,
  showNotificationFilterAsChips,
  showForYouNotificationsTab,
}: NotificationsPanelProps) => {
  const isCompactMode = useIsCompactMode();
  return (
    <PanelPortal enableAnimations id='notifications' width='medium' onClose={onClose}>
      <SurfaceHeader.Root noBorder css={{ paddingBottom: 0 }}>
        <SurfaceHeader.Row>
          {isCompactMode && (
            <SurfaceHeader.Start>
              <SurfaceHeader.Icon iconName='caret-left-small' onClick={onClose} />
            </SurfaceHeader.Start>
          )}
          <SurfaceHeader.Title>{labelMap['notifications']}</SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <BulkNotificationHistoryActions
              hideFilterIcon={showNotificationFilterAsChips || showForYouNotificationsTab}
            />
            {!isCompactMode && (
              <>
                <SurfaceHeader.Divider />
                <SurfaceHeader.Icon iconName='x' onClick={onClose} />
              </>
            )}
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>
      <PanelContent css={{ padding: 0 }}>
        <NotificationTray closeModal={onClose} selectLocationTray={onLocationTrayOpen} />
      </PanelContent>
    </PanelPortal>
  );
};

export const NotificationsPanelWrapper = () => {
  const { setNotificationsOpen, isNotificationsOpen, handleSelectableIconClick } = useTopBarShallowStore(
    'setNotificationsOpen',
    'isNotificationsOpen',
    'handleSelectableIconClick'
  );
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const showForYouNotificationsTab = getFeatureFlagValue('for-you-notifications');
  const showNotificationFilterAsChips = getFeatureFlagValue('notification-filter-chips');

  const handleClose = useCallback(() => {
    setNotificationsOpen(false);
  }, [setNotificationsOpen]);

  return (
    isNotificationsOpen && (
      <NotificationsPanel
        onClose={handleClose}
        onLocationTrayOpen={(e) => {
          handleSelectableIconClick('location', e);
        }}
        showNotificationFilterAsChips={showNotificationFilterAsChips}
        showForYouNotificationsTab={showForYouNotificationsTab}
      />
    )
  );
};
